import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, IconButton, Link, Stack } from '@mui/material';
import Alert, { AlertColor } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaCopy } from 'react-icons/fa';
import { configureVms, VmsType, VmsAccount, validateOtp, resendVmsOtp } from 'wisestep-api';
import {
  ControlledTextInput,
  IWorkspaceContext,
  WsButton,
  WsTypoColors,
  WsTypography,
  WsColors,
  WsDialog,
  ChatBoxHeader,
  WsTypoVariants,
} from 'wisestep-base';

import AccountManagerUserFilter from '../../../../../components/filters/components/AccountManagerFilter';
import useCopyToClipboard from '../../../../../features/candidates/talentpool/hooks/useCopyToClipboard';
import verifiedIcon from '../../../../../images/verified.png';
import { extractEmail } from '../../../../../utils/StringUtils';
import { configureVmsPayload } from '../../helper/payload';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  width: '35%',
  borderRadius: 1,
};

const broderStyle = {
  border: '1px solid #00b4d8',
  borderRadius: 1,
  width: '100%',
};
const borderStyleForEnabled = {
  borderRadius: 1,
  width: '100%',
};

const gridStyle = {
  m: 1,
};

const textFieldInputProps = {
  style: {
    padding: '2px',
    height: '30px',
    maxWidth: '100%',
    marginLeft: '10px',
  },
};

export interface ICreateVMSModal {
  open: boolean;
  editMode: boolean;
  selectedVmsAccount: VmsAccount;
  handleClose: () => void;
  onSuccess: () => void;
  selectedVms: VmsType;
  openAddClient: () => void;
  setCurrentVmsAccount: (currentVmsAccount: VmsAccount) => void;
  getCurrentVmsAccount: () => VmsAccount;
  currentWorkspace: IWorkspaceContext | undefined;
  vmsDomain: string | undefined;
}

interface VmsFormData {
  id: string;
  vms: string;
  accountName: string;
  subscription: string;
  forwardTo: string;
  loginUrl: string;
  whitelistEmail: string;
  notificationEmailList: string;
  otp: string;
  accountPassword: string;
  userName: string;
  frequency: string;
  organisationKey: string;
  accountManagers: string[];
}

interface SnackBarData {
  severity: AlertColor;
  message: string;
}

const ConfigureVmsDialog = ({
  open,
  editMode,
  selectedVmsAccount,
  handleClose,
  onSuccess,
  selectedVms,
  openAddClient,
  setCurrentVmsAccount,
  getCurrentVmsAccount,
  currentWorkspace,
  vmsDomain,
}: ICreateVMSModal) => {
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [isVMSPlanExpired, setIsVmsPlanExpired] = useState(false);
  const [isVmsAccountVerified, setIsVmsAccountVerified] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackBar] = useState<boolean>(false);
  const [snackBarData, setSnackBarData] = useState<SnackBarData>();
  const methods = useForm<VmsFormData>({
    defaultValues: {
      id: editMode ? selectedVmsAccount.id : '',
      accountName: editMode ? selectedVmsAccount.accountName : '',
      accountManagers: editMode
        ? selectedVmsAccount.accountManagers === null
          ? []
          : selectedVmsAccount.accountManagers
        : [],
      accountPassword: editMode ? selectedVmsAccount.accountPassword : '',
      loginUrl: editMode ? selectedVmsAccount.loginUrl : '',
      whitelistEmail: editMode ? selectedVmsAccount.whiteListEmail : '',
      notificationEmailList: editMode ? selectedVmsAccount.notificationEmailList : '',
      subscription: 'Standard',
      forwardTo: currentWorkspace
        ? selectedVms.text + '@' + currentWorkspace?.link + '.' + vmsDomain
        : '',
    },
  });
  const [, copy] = useCopyToClipboard();
  const emailBasedVms = ['PROWAND_PRO', 'BEELINE'];
  const vectorBasedVms = ['VECTOR'];
  const covendisBasedVms = ['COVENDIS'];

  const handleVmsCreation = () => {
    setIsVmsAccountVerified(false);
    methods.reset();
    handleClose();
    if (!editMode) {
      openAddClient();
    }
  };

  const sendOtp = async () => {
    const accountManagerIds: string[] = methods.getValues('accountManagers');
    let currentVmsAccount: VmsAccount = {
      ...configureVmsPayload,
      accountName: methods.getValues('accountName'),
      accountManagers: accountManagerIds.map((email) => extractEmail(email)),
      forwardTo: methods.getValues('forwardTo'),
      loginUrl: emailBasedVms.includes(selectedVms.name) ? '' : methods.getValues('loginUrl'),
      accountPassword: emailBasedVms.includes(selectedVms.name)
        ? ''
        : methods.getValues('accountPassword'),
      subscription: 'Standard',
      vms: selectedVms.name,
      accountClient: [],
      whiteListEmail: methods.getValues('whitelistEmail'),
      notificationEmailList: methods.getValues('notificationEmailList'),
      id: methods.getValues('id'),
      userName: methods.getValues('userName'),
      frequency: methods.getValues('frequency'),
      organisationKey: methods.getValues('organisationKey'),
    };
    try {
      const response = await configureVms([currentVmsAccount]);
      setOtpSent(true);
      onSuccess();
      currentVmsAccount = response.data[0];
      setCurrentVmsAccount(currentVmsAccount);
    } catch (e) {
      const err = e as AxiosError;
      err.response?.status === 402 && setIsVmsPlanExpired(true);
    }
  };

  const verifyVmsOtp = async () => {
    const dto: VmsAccount = {
      ...getCurrentVmsAccount(),
      otp: methods.getValues('otp'),
    };
    const response = await validateOtp(dto);
    setIsVmsAccountVerified(response.data);
    if (response.data) {
      showSnackBar('success', 'VMS successfully configured.');
    } else {
      showSnackBar('error', 'Invalid OTP! Please enter a valid OTP.');
    }
    onSuccess();
  };

  const resendOtp = async () => {
    const dto: VmsAccount = {
      ...getCurrentVmsAccount(),
    };
    const response = await resendVmsOtp(dto);
    if (response.status === 200) {
      showSnackBar('success', 'OTP resent successfully.');
    } else {
      showSnackBar('error', 'Error while resending OTP.');
    }
  };

  const showSnackBar = (status: string, message: string) => {
    if (status === 'success') {
      const obj: SnackBarData = {
        severity: 'success',
        message: message,
      };
      setSnackBarData(obj);
    } else {
      const obj: SnackBarData = {
        severity: 'error',
        message: message,
      };
      setSnackBarData(obj);
    }
    setOpenSnackBar(true);
  };

  const hideSnackBar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackBar(false);
  };

  const closeDialog = () => {
    setIsVmsPlanExpired(false);
    methods.reset();
    handleClose();
    setOtpSent(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      {!isVMSPlanExpired ? (
        <FormProvider {...methods}>
          <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={hideSnackBar}>
            <Alert onClose={hideSnackBar} severity={snackBarData?.severity} sx={{ width: '100%' }}>
              {snackBarData?.message}
            </Alert>
          </Snackbar>
          <Box sx={style} component={'form'} onSubmit={methods.handleSubmit(handleVmsCreation)}>
            <Grid xs={12}>
              <Grid
                container
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={gridStyle}
                xs={12}
              >
                <Typography id="modal-modal-title" variant="h6" component="h2" color="#00B4D8">
                  Configure {selectedVms.label}
                </Typography>
              </Grid>
            </Grid>
            <Grid mt={2} rowSpacing={2} direction="row" container>
              <Grid container item>
                <Grid display="flex" justifyContent="center" item xs={4}>
                  <WsTypography color={WsTypoColors.SECONDARY}>Subscription:</WsTypography>
                </Grid>
                <Grid display="flex" item xs={8}>
                  <ControlledTextInput
                    sx={broderStyle}
                    focused
                    size="small"
                    name="subscription"
                    id="vms-subscription"
                    variant="outlined"
                    required
                    disabled
                    inputProps={textFieldInputProps}
                  />
                </Grid>
              </Grid>
              {!vectorBasedVms.includes(selectedVms.name) &&
                !covendisBasedVms.includes(selectedVms.name) && (
                  <Grid container item>
                    <Grid display="flex" justifyContent="center" item xs={4}>
                      <WsTypography color={WsTypoColors.SECONDARY}>Forwarded To:</WsTypography>
                    </Grid>
                    <Grid item xs={7}>
                      <ControlledTextInput
                        sx={broderStyle}
                        size="small"
                        name="forwardTo"
                        id="vms-forwardedTo"
                        variant="outlined"
                        focused
                        type="email"
                        disabled
                        required
                        inputProps={textFieldInputProps}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton onClick={() => copy(methods.getValues('forwardTo'))}>
                        <FaCopy size={25} color={WsColors.TheBlues500} />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
              {!vectorBasedVms.includes(selectedVms.name) &&
                !covendisBasedVms.includes(selectedVms.name) &&
                !emailBasedVms.includes(selectedVms.name) && (
                  <Grid container item>
                    <Grid display="flex" justifyContent="center" item xs={4}>
                      <WsTypography color={WsTypoColors.SECONDARY}>Login URL:</WsTypography>
                    </Grid>
                    <Grid display="flex" item xs={8}>
                      <ControlledTextInput
                        sx={borderStyleForEnabled}
                        size="medium"
                        name="loginUrl"
                        id="vms-loginUrl"
                        variant="outlined"
                        focused
                        type="text"
                        inputProps={textFieldInputProps}
                      />
                    </Grid>
                  </Grid>
                )}
              <Grid container item>
                <Grid display="flex" justifyContent="center" item xs={4}>
                  <WsTypography color={WsTypoColors.SECONDARY}>Account Name:</WsTypography>
                </Grid>
                <Grid display="flex" item xs={8}>
                  <ControlledTextInput
                    sx={borderStyleForEnabled}
                    name="accountName"
                    id="account-name"
                    variant="outlined"
                    focused
                    required
                    fullWidth={false}
                    inputProps={textFieldInputProps}
                  />
                </Grid>
              </Grid>
              {(vectorBasedVms.includes(selectedVms.name) ||
                covendisBasedVms.includes(selectedVms.name)) && (
                <Grid container item>
                  <Grid display="flex" justifyContent="center" item xs={4}>
                    <WsTypography color={WsTypoColors.SECONDARY}>User Name:</WsTypography>
                  </Grid>
                  <Grid display="flex" item xs={8}>
                    <ControlledTextInput
                      sx={borderStyleForEnabled}
                      name="userName"
                      id="user-name"
                      variant="outlined"
                      focused
                      required
                      fullWidth={false}
                      inputProps={textFieldInputProps}
                    />
                  </Grid>
                </Grid>
              )}
              {!emailBasedVms.includes(selectedVms.name) && (
                <Grid container item>
                  <Grid display="flex" justifyContent="center" item xs={4}>
                    <WsTypography color={WsTypoColors.SECONDARY}>Password:</WsTypography>
                  </Grid>
                  <Grid display="flex" item xs={8}>
                    <ControlledTextInput
                      sx={borderStyleForEnabled}
                      name="accountPassword"
                      size="small"
                      id="vms-accountPassword"
                      variant="outlined"
                      focused
                      type="password"
                      required
                      inputProps={textFieldInputProps}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container item>
                <Grid display="flex" justifyContent="center" item xs={4}>
                  <WsTypography color={WsTypoColors.SECONDARY}>Account Manager:</WsTypography>
                </Grid>
                <Grid display="flex" item xs={8}>
                  <AccountManagerUserFilter
                    name="accountManagers"
                    multiple={true}
                    isBorderStyle={true}
                  />
                </Grid>
              </Grid>
              {(vectorBasedVms.includes(selectedVms.name) ||
                covendisBasedVms.includes(selectedVms.name)) && (
                <Grid container item>
                  <Grid display="flex" justifyContent="center" item xs={4}>
                    <WsTypography color={WsTypoColors.SECONDARY}>Frequency:</WsTypography>
                  </Grid>
                  <Grid display="flex" item xs={8}>
                    <ControlledTextInput
                      sx={borderStyleForEnabled}
                      name="frequency"
                      id="frequency"
                      variant="outlined"
                      focused
                      required
                      fullWidth={false}
                      inputProps={textFieldInputProps}
                      placeholder="Enter digits only"
                    />
                  </Grid>
                </Grid>
              )}
              {(vectorBasedVms.includes(selectedVms.name) ||
                covendisBasedVms.includes(selectedVms.name)) && (
                <Grid container item>
                  <Grid display="flex" justifyContent="center" item xs={4}>
                    <WsTypography color={WsTypoColors.SECONDARY}>Organisation Key:</WsTypography>
                  </Grid>
                  <Grid display="flex" item xs={8}>
                    <ControlledTextInput
                      sx={borderStyleForEnabled}
                      name="organisationKey"
                      id="organisation-Key"
                      variant="outlined"
                      focused
                      required
                      fullWidth={false}
                      inputProps={textFieldInputProps}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid spacing={1} container item>
                <Grid display="flex" justifyContent="center" item xs={4}>
                  <WsTypography color={WsTypoColors.SECONDARY}>VMS Alert Email:</WsTypography>
                </Grid>
                <Grid item xs={5}>
                  <ControlledTextInput
                    sx={borderStyleForEnabled}
                    name="whitelistEmail"
                    size="small"
                    id="vms-whiteList"
                    variant="outlined"
                    focused
                    type="email"
                    required
                    inputProps={textFieldInputProps}
                  />
                </Grid>
                <Grid item xs={3}>
                  {!otpSent && (
                    <WsButton variant="outlined" type="button" onClick={() => sendOtp()}>
                      Send OTP
                    </WsButton>
                  )}
                  {otpSent && !isVmsAccountVerified && (
                    <WsButton onClick={resendOtp} variant="outlined">
                      Resend OTP
                    </WsButton>
                  )}
                  {isVmsAccountVerified && (
                    <IconButton>
                      <img src={verifiedIcon} width="15px" height="15px" alt="verified icon" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
              {otpSent && (
                <Grid spacing={1} container item>
                  <Grid display="flex" justifyContent="center" item xs={4}>
                    <WsTypography color={WsTypoColors.SECONDARY}>OTP:</WsTypography>
                  </Grid>
                  <Grid item xs={5}>
                    <ControlledTextInput
                      sx={borderStyleForEnabled}
                      size="small"
                      name="otp"
                      id="standard-basic"
                      variant="outlined"
                      focused
                      required
                      inputProps={textFieldInputProps}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    {otpSent && !isVmsAccountVerified && (
                      <WsButton variant="outlined" type="button" onClick={() => verifyVmsOtp()}>
                        Verify OTP
                      </WsButton>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid container item>
                <Grid display="flex" justifyContent="center" item xs={4}>
                  <WsTypography color={WsTypoColors.SECONDARY}>Notification Email:</WsTypography>
                </Grid>
                <Grid display="flex" item xs={8}>
                  <ControlledTextInput
                    sx={borderStyleForEnabled}
                    name="notificationEmailList"
                    size="small"
                    id="vms-notificationEmailList"
                    variant="outlined"
                    focused
                    type="text"
                    required
                    inputProps={textFieldInputProps}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid mt={2} pt={2} justifyContent="center" container item>
              <Stack spacing={3} direction="row">
                <WsButton variant="outlined" size="medium" onClick={closeDialog}>
                  Skip
                </WsButton>
                <WsButton
                  disabled={otpSent && isVmsAccountVerified ? false : true}
                  variant="contained"
                  size="medium"
                  type="submit"
                >
                  Save
                </WsButton>
              </Stack>
            </Grid>
          </Box>
        </FormProvider>
      ) : (
        <WsDialog size="sm" openDialog={isVMSPlanExpired} onClose={closeDialog}>
          <ChatBoxHeader>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} display="inline" bold={true}>
              VMS Configuration Limit Reached
            </WsTypography>
            <CloseIcon
              sx={{ cursor: 'pointer', float: 'right', mt: 0.5 }}
              fontSize={'small'}
              onClick={closeDialog}
            />
          </ChatBoxHeader>
          <DialogContent>
            <Stack spacing={1}>
              <Box>
                <WsTypography display="inline">{`You have  reached the maximum VMS configurations for your account.`}</WsTypography>
              </Box>
              <Box>
                <WsTypography display="inline">
                  To add more VMS accounts, either delete any existing accounts or contact our sales
                  team on
                </WsTypography>
                <Link sx={{ ml: 1, mr: 1 }} href="mailto:sales@wisestep-inc.com">
                  sales@wisestep-inc.com
                </Link>
                <WsTypography display="inline">to upgrade your accounts</WsTypography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <WsButton variant="outlined" onClick={closeDialog}>
              Close
            </WsButton>
          </DialogActions>
        </WsDialog>
      )}
    </Modal>
  );
};
export default ConfigureVmsDialog;
